import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Search, X } from "lucide-react";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { FadeUp } from "../components/Reveal";
import { EmSwash } from "../components/EmSwash";
import { faqsFor, searchFaqs } from "../components/faq/faqGroups";
import { CategoryBrowser, QuestionRow } from "../components/faq/FaqBrowser";
import { SEO } from "../lib/SEO";
import { LitGround } from "../components/PaperLight";

/** /faq — three categories (everyone / students / donors) with search. */
export default function FAQPage() {
  const [group, setGroup] = useState(() => {
    const g = new URLSearchParams(window.location.search).get("group");
    return ["general", "students", "donors"].includes(g) ? g : "general";
  });
  const [query, setQuery] = useState(() => new URLSearchParams(window.location.search).get("q") || "");
  const results = useMemo(() => searchFaqs(query), [query]);
  const searching = query.trim().length >= 2;
  const [openResult, setOpenResult] = useState(0);
  // Deep link: /faq?group=students&open=expense-guidelines starts with that question expanded and scrolled into view.
  const [deepLink] = useState(() => new URLSearchParams(window.location.search).get("open"));
  const initialOpen = Math.max(0, faqsFor(group).findIndex((f) => f.id === deepLink));
  useEffect(() => {
    if (!deepLink) return;
    const t = setTimeout(() => document.getElementById(deepLink)?.scrollIntoView({ behavior: "smooth", block: "start" }), 350);
    return () => clearTimeout(t);
  }, [deepLink]);

  return (
    <main data-testid="faq-page" className="bg-sand">
      <SEO
        title="FAQs — My Rich Aunt"
        description="Answers about how My Rich Aunt works: who can create a campaign, how gifts become grants, tax deductibility, verification, and more."
        path="/faq"
      />
      <Navbar onStart={() => (window.location.href = "/")} primaryLabel="Back to home" />

      <LitGround>
      <section className="mx-auto max-w-7xl px-6 pt-32 lg:px-8 lg:pt-44">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-12 lg:items-end lg:gap-16">
          <div className="lg:col-span-7">
            <FadeUp>
              <h1 data-testid="faq-headline" className="max-w-3xl font-serif text-[3.5rem] lg:text-[5.5rem] leading-[0.98] tracking-tight text-ink">
                Everything you might <EmSwash>ask us.</EmSwash>
              </h1>
            </FadeUp>
            <FadeUp delay={0.06}>
              <p className="mt-6 max-w-md text-base leading-relaxed text-olive" data-testid="faq-lead">
                The questions students, donors and partners ask us most, answered plainly.
              </p>
            </FadeUp>
          </div>
          <FadeUp delay={0.1} className="lg:col-span-5">
            <label className="relative block" data-testid="faq-search">
              <span className="text-[13px] font-semibold uppercase tracking-[0.22em] text-olive">Type a question</span>
              <span className="relative mt-2 block">
                <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-olive" strokeWidth={2} />
                <input
                  type="search"
                  value={query}
                  onChange={(e) => { setQuery(e.target.value); setOpenResult(0); }}
                  placeholder="tax deductible, who can apply, general fund…"
                  data-testid="faq-search-input"
                  className="w-full rounded-full border border-border bg-bone py-3.5 pl-11 pr-11 font-sans text-base text-ink placeholder:text-ink/35 focus:border-ink focus:outline-none focus:ring-2 focus:ring-ink/10 [&::-webkit-search-cancel-button]:appearance-none [&::-webkit-search-decoration]:appearance-none"
                />
                {query && (
                  <button type="button" onClick={() => setQuery("")} aria-label="Clear search" data-testid="faq-search-clear" className="absolute right-3 top-1/2 inline-flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-full text-olive hover:bg-ink/10 hover:text-ink">
                    <X className="h-4 w-4" strokeWidth={2} />
                  </button>
                )}
              </span>
            </label>
          </FadeUp>
        </div>
        <div className="mt-10 h-px w-full bg-ink/15" />
      </section>
      </LitGround>

      <section className="mx-auto max-w-7xl px-6 pt-10 lg:px-8 lg:pt-12">
        <AnimatePresence mode="wait">
          {searching ? (
            <motion.div key="search" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.2 }} data-testid="faq-search-results">
              <p className="text-[13px] text-olive" data-testid="faq-search-count">
                {results.length === 0
                  ? "No match yet."
                  : `${results.length} ${results.length === 1 ? "answer" : "answers"} for “${query.trim()}”`}
              </p>
              {results.length > 0 ? (
                <ul className="mt-6 border-t border-ink/10">
                  {results.map((f, i) => (
                    <QuestionRow key={f.q} f={f} i={i} showGroup open={openResult === i} onToggle={() => setOpenResult(openResult === i ? -1 : i)} />
                  ))}
                </ul>
              ) : (
                <p className="mt-4 max-w-md font-serif text-3xl leading-snug text-ink" data-testid="faq-search-empty">
                  We haven't written that one down yet. Send it our way below.
                </p>
              )}
            </motion.div>
          ) : (
            <motion.div key="browse" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.2 }}>
              <CategoryBrowser items={faqsFor(group)} activeKey={group} onPick={setGroup} initialOpen={initialOpen} />
            </motion.div>
          )}
        </AnimatePresence>

        <FadeUp>
          <p className="mt-16 max-w-md text-base leading-relaxed text-olive lg:mt-20" data-testid="faq-contact-line">
            Don't see yours?{" "}
            <Link to="/contact" className="font-semibold text-ink underline decoration-tangerine decoration-2 underline-offset-4 hover:text-tangerine" data-testid="faq-contact-link">
              Send us a note
            </Link>.
          </p>
        </FadeUp>
      </section>

      <Footer />
    </main>
  );
}
